import { type ClassValue, clsx } from 'clsx'
import path from 'path-browserify'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function resolveRoutePath(basePath?: string, routePath?: string) {
  return basePath ? path.resolve(basePath, routePath ?? '') : routePath ?? ''
}

/**
 * 业务状态统一样式映射。
 * 调查记录的“记录状态”和“客户状态”都统一走这个函数。
 * 固定枚举优先精确匹配；其余项目已有通用状态继续保持原语义映射。
 */
const businessStatusClassMap: Record<string, string> = {
  // 调查记录：记录状态
  PENDING: 'app-status-tag--pending',
  COMPLETED: 'app-status-tag--completed',
  TERMINATED: 'app-status-tag--terminated',
  QUOTA_FULL: 'app-status-tag--quota-full',
  SAFE_TERMINATED: 'app-status-tag--safe-terminated',

  // 调查记录：客户状态
  APPROVED: 'app-status-tag--approved',
  REJECTED: 'app-status-tag--rejected',
  RECONCILIATION_APPROVED: 'app-status-tag--reconciliation-approved',
  RECONCILIATION_REJECTED: 'app-status-tag--reconciliation-rejected',
  ABANDON: 'app-status-tag--abandon',
  PREVIOUSLY_APPROVED: 'app-status-tag--previously-approved',
}

export function getBusinessStatusClass(status?: string | null) {
  const value = String(status || '').trim().toUpperCase()

  if (businessStatusClassMap[value]) {
    return businessStatusClassMap[value]
  }

  // 保留项目其他页面已有的通用状态语义，避免公共函数改造影响现有页面。
  if (['P', 'WAITING', 'NOT_STARTED'].includes(value)) {
    return 'app-status-tag--pending'
  }
  if (['REVIEW', 'REVIEWING', 'PENDING_REVIEW', 'PENDING_AUDIT'].includes(value)) {
    return 'app-status-tag--review'
  }
  if (['C', 'COMPLETE', 'SUCCESS', 'PAID'].includes(value)) {
    return 'app-status-tag--success'
  }
  if (['PROJECT_QUOTA_FULL', 'INVOICED', 'PROCESSING'].includes(value)) {
    return 'app-status-tag--warning'
  }
  if (['S', 'FAILED', 'ERROR'].includes(value)) {
    return 'app-status-tag--danger'
  }
  if (['Q', 'QUALITY_REFUND'].includes(value)) {
    return 'app-status-tag--purple'
  }
  if (['T', 'RUNNING', 'SENT', 'DELIVERED'].includes(value)) {
    return 'app-status-tag--cyan'
  }

  return 'app-status-tag--info'
}

/**
 * 调查记录终止/跳转原因统一样式映射。
 * redirectEnum 的 21 个固定状态一一对应独立颜色。
 */
const reasonStatusClassMap: Record<string, string> = {
  PASS: 'app-reason-tag--pass',
  RECONCILIATION: 'app-reason-tag--reconciliation',
  TERMINATED: 'app-reason-tag--client-screening',
  REPEATENTRY: 'app-reason-tag--repeat-entry',
  PROJECT_CLOSED: 'app-reason-tag--project-closed',
  NOT_PROJECT: 'app-reason-tag--project-not-found',
  IP_REPEAT: 'app-reason-tag--ip-repeat',
  IP_BLOCKED: 'app-reason-tag--ip-blocked',
  COUNTRY_NOT_MATCH: 'app-reason-tag--country-not-match',
  PROJECT_NOT_START: 'app-reason-tag--project-not-start',
  TERMINATED_PII: 'app-reason-tag--profile-not-match',
  TERMINATED_HOUR_LIMIT: 'app-reason-tag--participation-limit',
  TERMINATED_HOUR_COMPLETE: 'app-reason-tag--completion-limit',
  PROJECT_QUOTA_FULL: 'app-reason-tag--project-quota-full',
  TERMINATED_IR: 'app-reason-tag--ir-terminated',
  TERMINATED_TIMEOUT: 'app-reason-tag--timeout',
  TERMINATED_SPEED_LIMIT: 'app-reason-tag--speed-limit',
  DEVICE_QUOTA_FULL: 'app-reason-tag--device-quota-full',
  DEVICE_NOT_SUPPORTED: 'app-reason-tag--device-not-supported',
  PII_COUNTRY_NOT_MATCH: 'app-reason-tag--pii-country-not-match',
  QUALITY_REFUND: 'app-reason-tag--quality-refund',
}

export function getReasonStatusClass(reason?: string | null) {
  const value = String(reason || '').trim().toUpperCase()
  return reasonStatusClassMap[value] || 'app-reason-tag--neutral'
}
